export const POSTS = [
  {
    id: "why-your-business-needs-a-brand-before-a-website",
    title: "Why Your Business Needs a Brand Before a Website",
    date: "September 2026",
    category: "Branding",
    readTime: "4 min read",
    excerpt: "Most businesses build a website before they have a brand. That's backwards. Here's why your brand has to come first — and what happens when it doesn't.",
    hero: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1400&q=80",
    body: [
      {
        type: "p",
        text: "Every week we speak to businesses that want a website. They have a rough idea of what it should look like. They want something 'clean and professional'. But when we ask what the brand stands for, what makes them different from competitors, or who their ideal customer actually is — there's silence.",
      },
      {
        type: "p",
        text: "The website isn't the problem. The absence of a brand underneath it is.",
      },
      {
        type: "h2",
        text: "A website is a container. Brand is what goes inside it.",
      },
      {
        type: "p",
        text: "Think of your website as a storefront window. You can design the most beautiful window display in the world — but if there's nothing behind it that makes people feel something, they'll walk past. Brand is what gives a business a personality, a point of view, a reason to exist beyond the product or service it sells.",
      },
      {
        type: "p",
        text: "When you build a website without brand strategy, you end up with a site that looks fine but doesn't convert. It doesn't say the right things to the right people. It doesn't differentiate. It doesn't build trust. It just exists.",
      },
      {
        type: "h2",
        text: "What brand strategy actually means",
      },
      {
        type: "p",
        text: "Brand strategy isn't about choosing a font or picking a colour palette. It's about answering the questions that determine everything else: Who are you building this for? What problem do you solve better than anyone else? What do you want people to feel when they interact with your business? What's your tone of voice?",
      },
      {
        type: "p",
        text: "Once those questions are answered, every design decision — website layout, colour choice, photography style, the words on your homepage — flows from a single coherent idea. That coherence is what makes a business look premium and feel trustworthy.",
      },
      {
        type: "h2",
        text: "The cost of skipping it",
      },
      {
        type: "p",
        text: "Businesses that build websites without brand foundations usually end up rebuilding them within two years. The site doesn't perform. The messaging doesn't resonate. The design feels disconnected from the actual business. So they start over — spending double what they would have spent getting it right the first time.",
      },
      {
        type: "p",
        text: "The smarter path: do the brand work first. It takes a week or two. It costs less than a full website rebuild. And it makes every other investment — website, content, ads — significantly more effective.",
      },
      {
        type: "cta",
        text: "Need help building a brand that works? Let's talk.",
      },
    ],
  },
  {
    id: "how-to-choose-a-digital-marketing-agency-in-india",
    title: "How to Choose a Digital Marketing Agency in India",
    date: "September 2026",
    category: "Strategy",
    readTime: "5 min read",
    excerpt: "There are thousands of digital agencies in India. Most of them will take your money and deliver dashboards. Here's how to find one that actually grows your business.",
    hero: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1400&q=80",
    body: [
      {
        type: "p",
        text: "The Indian digital marketing industry has exploded. There are agencies for every budget, every service and every niche. And most founders have at least one horror story — money spent, results absent, no real accountability.",
      },
      {
        type: "p",
        text: "Choosing the wrong agency is expensive. Not just in money — in time, in missed opportunity and in the frustration of having to start over. Here's what actually separates a good agency from a bad one.",
      },
      {
        type: "h2",
        text: "1. They ask questions before they pitch",
      },
      {
        type: "p",
        text: "A good agency wants to understand your business before they suggest anything. A bad agency shows up with a proposal already written. If someone sends you a 'social media package' before they've asked about your product, your margins, your customers or your goals — that's a red flag.",
      },
      {
        type: "h2",
        text: "2. They talk about business outcomes, not vanity metrics",
      },
      {
        type: "p",
        text: "Likes, followers and impressions are easy to generate and nearly impossible to spend. Revenue, leads, enquiries and cost-per-acquisition are the numbers that matter. Any agency that can't clearly explain how their work connects to your business results isn't the right partner.",
      },
      {
        type: "h2",
        text: "3. They're honest about what they can't do",
      },
      {
        type: "p",
        text: "No agency is excellent at everything. A branding agency is not necessarily a performance marketing agency. A social media team is not necessarily a web development team. The best agencies know exactly what they're great at and are transparent when something is outside their zone. Be wary of agencies that claim to be world-class at 15 different things.",
      },
      {
        type: "h2",
        text: "4. They have a clear point of view",
      },
      {
        type: "p",
        text: "Generic agencies produce generic work. The best agencies have opinions — about strategy, about what works for your kind of business, about what they won't do and why. A partner who thinks clearly about your business is more valuable than one who simply executes whatever you ask.",
      },
      {
        type: "h2",
        text: "5. They're accessible and responsive",
      },
      {
        type: "p",
        text: "A lot breaks down in execution. Timelines slip, approvals get delayed, campaigns need adjusting. The agency you choose needs to be easy to communicate with — not a three-day email chain every time you have a question.",
      },
      {
        type: "cta",
        text: "Looking for a digital agency that fits? Start a conversation with OVARD.",
      },
    ],
  },
  {
    id: "why-most-social-media-marketing-fails",
    title: "Why Most Social Media Marketing Fails (And What Actually Works)",
    date: "September 2026",
    category: "Content & Social",
    readTime: "4 min read",
    excerpt: "Businesses pour money into social media and get almost nothing back. The problem isn't the platform — it's the strategy. Or rather, the absence of one.",
    hero: "https://images.unsplash.com/photo-1611262588024-d12430b98920?w=1400&q=80",
    body: [
      {
        type: "p",
        text: "Ask most business owners how their social media is performing and you'll get one of two answers. Either 'we're not really doing much' or 'we post regularly but it doesn't seem to do anything'. Both are symptoms of the same underlying problem: activity without strategy.",
      },
      {
        type: "h2",
        text: "The posting trap",
      },
      {
        type: "p",
        text: "Most businesses approach social media like a publishing schedule. Post three times a week. Use trending audio. Add hashtags. Keep the feed looking consistent. And then wonder why the followers don't convert into customers.",
      },
      {
        type: "p",
        text: "The posting trap is thinking that frequency equals impact. It doesn't. Ten mediocre posts have less value than one piece of content that genuinely connects with your audience, solves a problem they have or shows your work in a way that makes someone want to reach out.",
      },
      {
        type: "h2",
        text: "What actually drives results",
      },
      {
        type: "p",
        text: "Social media that works for a business starts with a clear answer to one question: what do we want the person watching this to do next? If the answer is 'follow us', that's not a business goal. If the answer is 'message us about their project' or 'visit our website to see our portfolio' or 'trust us enough to book a call' — now you have something to build content around.",
      },
      {
        type: "h2",
        text: "Content that converts vs content that fills a feed",
      },
      {
        type: "p",
        text: "Converting content is specific. It shows real work. It speaks directly to a type of person with a specific problem. It gives enough value that the viewer feels something — informed, inspired, understood — and then includes a clear next step.",
      },
      {
        type: "p",
        text: "Feed-filling content is generic. It's motivational quotes, reposted industry news and product photos with no context. It might get some likes from existing followers. It almost never brings in new customers.",
      },
      {
        type: "h2",
        text: "The fix",
      },
      {
        type: "p",
        text: "Before posting anything, get clear on your audience, your objective and the one action you want them to take. Then build content that serves that purpose — consistently, not frantically. Quality beats frequency every time.",
      },
      {
        type: "cta",
        text: "Want a content strategy that actually works for your business? Let's figure it out.",
      },
    ],
  },
];
